import React, { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Clipboard as ClipboardIcon, Package as PackageIcon } from "lucide-react";
import MiniMonth from "../MiniMonth";

// Left-hand sidebar of the Calendar view. Stack:
//   1. Current-month MiniMonth (with prev/next nav)
//   2. Clipboard panel — appointments the operator lifted off the grid
//   3. Next-month MiniMonth (no nav, shares the same cursor)
//
// The clipboard card pulses briefly after "Move to Clipboard" fires so the
// operator can find their appointment without scanning the sidebar.
//
// Bulk Clipboard Grouping (Iter-324): entries carrying the same
// `package_id` (per same-customer cohort) are grouped into a single
// header card titled with the Package Name. Only the package header
// is draggable; sub-services are listed inside for context.
export default function CalendarSidebar({
  monthCursor, isoLocal, onSelectDate, onShiftMonth,
  clipboardCardRef, clipboardPulse, clipboard, setDragActive, packages = [],
}) {
  const nextMonth = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1);

  // Group by package_id + customer_id — same cohort rule as the
  // package cascade on the backend. Solo entries (no package_id)
  // render as before.
  const grouped = useMemo(() => {
    const out = [];
    const byKey = new Map();
    for (const a of clipboard) {
      if (a.package_id && a.customer_id) {
        const key = `pkg::${a.package_id}::${a.customer_id}`;
        if (!byKey.has(key)) {
          const bucket = {
            kind: "package",
            key,
            package_id: a.package_id,
            customer_id: a.customer_id,
            customer_name: a.customer_name,
            items: [],
          };
          byKey.set(key, bucket);
          out.push(bucket);
        }
        byKey.get(key).items.push(a);
      } else {
        out.push({ kind: "solo", key: `solo::${a.id}`, appt: a });
      }
    }
    return out;
  }, [clipboard]);  const packageNameFor = (pid) => {
    const p = (packages || []).find((x) => x.id === pid);
    return (p && p.name) || "Package";
  };

  return (
    <aside className="space-y-0.5 min-w-0" data-testid="calendar-sidebar-compact">
      <Card className="rounded-lg w-full max-w-[160px] mx-auto">
        <CardContent className="px-2.5 py-1">
          <MiniMonth
            year={monthCursor.getFullYear()}
            month={monthCursor.getMonth()}
            selectedIso={isoLocal}
            onSelect={onSelectDate}
            onShiftMonth={onShiftMonth}
            showNav="both"
          />
        </CardContent>
      </Card>

      {/* Clipboard — placed between the two mini-months so operators can
          see appointments after "Move to Clipboard" without scrolling. */}
      <Card
        ref={clipboardCardRef}
        className={
          "rounded-lg w-full max-w-[160px] mx-auto transition-shadow " +
          (clipboardPulse ? "ring-2 ring-primary shadow-lg" : "")
        }
        data-testid="clipboard-panel"
      >
        <CardContent className="px-2.5 py-1">
          <div className="flex items-center gap-2 mb-1.5 text-sm font-medium">
            <ClipboardIcon className="w-4 h-4" /> Clipboard
            <span className="ml-auto text-xs text-muted-foreground" data-testid="clipboard-count">{clipboard.length}</span>
          </div>
          {clipboard.length === 0 && (
            <p className="text-[11px] text-muted-foreground">
              Right-click any Appointment and select &ldquo;Move to Clipboard&rdquo; to use this feature
            </p>
          )}
          {grouped.map((row) => {
            if (row.kind === "package") {
              const pkgName = packageNameFor(row.package_id);
              return (
                <div
                  key={row.key}
                  draggable
                  onDragStart={(e) => {
                    // Serialise the whole package payload so the drop
                    // target can open the placement dialog without
                    // hitting the network again.
                    e.dataTransfer.setData("text/package-id", row.package_id);
                    e.dataTransfer.setData("text/package-customer-id", row.customer_id);
                    // Iter-325 · Anchor package drops directly at the
                    // mouse-Y so the placement popup pre-fills every
                    // start time from the visual drop position. NO
                    // `text/tile-offset-y` is emitted — the tiny
                    // clipboard tile's offset is meaningless once the
                    // ghost lands on the ~120px-tall calendar row and
                    // subtracting it caused the +15/-15 minute drift
                    // the operator hit on busy hours.
                    const firstServiceId = row.items[0]?.service_id;
                    setDragActive({ kind: "clipboard-package", service_id: firstServiceId, source_id: row.key });
                  }}
                  onDragEnd={() => setDragActive(null)}
                  className="border border-primary/40 rounded-md px-2 py-1.5 mb-1.5 text-xs cursor-grab bg-primary/5 hover:bg-primary/10"
                  data-testid={`clip-package-${row.package_id}-${row.customer_id}`}
                >
                  <div className="flex items-center gap-1 font-medium">
                    <PackageIcon className="w-3 h-3" />
                    <span className="truncate">{pkgName}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {row.customer_name || "Walk-in"}
                  </div>
                  <ul className="mt-1 pl-3 list-disc space-y-0.5 marker:text-muted-foreground">
                    {row.items.map((it) => (
                      <li
                        key={it.id}
                        className="text-[10px] text-muted-foreground truncate"
                        data-testid={`clip-package-item-${it.id}`}
                      >
                        {it.service_name}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            }
            const a = row.appt;
            return (
              <div
                key={a.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/appointment-id", a.id);
                  // Iter-325 · Top-Edge Anchoring — clipboard drops now
                  // land at the mouse-Y grid-slot directly (no
                  // `tile-offset-y` subtraction). A 60-min massage
                  // dropped anywhere in the 10:00–10:15 band snaps
                  // cleanly to 10:00 AM, matching the operator's
                  // visual expectation.
                  // Mirror the on-grid drag: broadcast the service_id so the
                  // day-grid can paint the grey-net overlay on staff columns
                  // that can't perform this service.
                  setDragActive({ kind: "clipboard", service_id: a.service_id, source_id: a.id });
                }}
                onDragEnd={() => setDragActive(null)}
                className="border border-border rounded-md px-2 py-1.5 mb-1.5 text-xs cursor-grab bg-secondary/50 hover:bg-secondary"
                data-testid={`clip-item-${a.id}`}
              >
                <div className="font-medium truncate">{a.customer_name || "Walk-in"}</div>
                <div className="text-muted-foreground truncate">{a.service_name}</div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="rounded-lg w-full max-w-[160px] mx-auto">
        <CardContent className="px-2.5 py-1">
          <MiniMonth
            year={nextMonth.getFullYear()}
            month={nextMonth.getMonth()}
            selectedIso={isoLocal}
            onSelect={onSelectDate}
            onShiftMonth={onShiftMonth}
            showNav="none"
          />
        </CardContent>
      </Card>
    </aside>
  );
}
